import React, { useState, lazy } from 'react';
import { ImageData } from '../utils/mockData';
import { SignificanceFlow } from './SignificanceFlow';
import { MiniDonutChart } from './MiniDonutChart';
import { FloatingLegend } from './FloatingLegend';
import { BayesianDistributionChart } from './BayesianDistributionChart';
interface StatisticalMatrixProps {
  data: ImageData[];
}
export const StatisticalMatrix: React.FC<StatisticalMatrixProps> = ({
  data
}) => {
  const [currentSignificance, setCurrentSignificance] = useState(95);
  const sortedData = [...data].sort((a, b) => b.techEnthusiast.stoppingPower - a.techEnthusiast.stoppingPower);
  const getById = (id: number) => data.find(d => d.id === id);
  // Lifestyle audience matrix, ordered as specified.
  const lifestyleRows = [{
    id: 1,
    z: null,
    bayes: 84.6,
    winner: true
  }, {
    id: 2,
    z: '0.079',
    bayes: 12.1,
    winner: false
  }, {
    id: 4,
    z: '0.012',
    bayes: 2.4,
    winner: false
  }, {
    id: 3,
    z: '0.004',
    bayes: 0.9,
    winner: false
  }];
  // Between-segment core evidence layer, ordered A, B, C, D.
  const betweenSegmentRows = [{
    id: 1,
    direction: 'Tech enthusiast significantly lower than lifestyle',
    p: '0.012'
  }, {
    id: 2,
    direction: 'Tech enthusiast significantly lower than lifestyle',
    p: '0.024'
  }, {
    id: 3,
    direction: 'Tech enthusiast significantly higher than lifestyle',
    p: '0.009'
  }, {
    id: 4,
    direction: 'Tech enthusiast significantly higher than lifestyle',
    p: '0.031'
  }];
  return <div className="space-y-8" data-id="element-366">
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-367">
        <h3 className="text-lg font-medium text-[#101010] mb-6" data-id="element-368">
          Explanation of Tests (Illustrative)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-369">
          <div className="p-4 bg-[#F9FAFB] rounded-sm border border-[#E5E5E5]" data-id="element-370">
            <h4 className="font-medium text-[#101010] mb-3 flex items-center" data-id="element-371">
              <span className="w-6 h-6 rounded-sm bg-[#C00000] text-white flex items-center justify-center text-sm mr-2" data-id="element-372">
                1
              </span>
              Within-Segment Testing
            </h4>
            <p className="text-sm text-[#404040]" data-id="element-373">
              Evaluates performance per audience (chi-square across all four
              assets, two-proportion z-test between winner and runner-up,
              Bayesian probability to be best).
            </p>
          </div>
          <div className="p-4 bg-[#F9FAFB] rounded-sm border border-[#E5E5E5]" data-id="element-374">
            <h4 className="font-medium text-[#101010] mb-3 flex items-center" data-id="element-375">
              <span className="w-6 h-6 rounded-sm bg-[#C00000] text-white flex items-center justify-center text-sm mr-2" data-id="element-376">
                2
              </span>
              Between-Segment Comparison
            </h4>
            <p className="text-sm text-[#404040]" data-id="element-377">
              The core evidence layer. For Asset A, behavioural interaction
              within the tech enthusiast segment was significantly lower than
              within the lifestyle segment (p &lt; 0.05). For Asset C, the
              reverse holds.
            </p>
          </div>
          <div className="p-4 bg-[#F9FAFB] rounded-sm border border-[#E5E5E5]" data-id="element-378">
            <h4 className="font-medium text-[#101010] mb-3 flex items-center" data-id="element-379">
              <span className="w-6 h-6 rounded-sm bg-[#C00000] text-white flex items-center justify-center text-sm mr-2" data-id="element-380">
                3
              </span>
              Confidence Summary
            </h4>
            <p className="text-sm text-[#404040]" data-id="element-381">
              All headline differences reported in this example meet a minimum
              85% confidence floor, key findings at 95%.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5] overflow-x-auto" data-id="element-382">
        <h3 className="text-lg font-medium text-[#101010] mb-4" data-id="element-383">
          Statistical Significance Matrix (Tech enthusiast audience)
        </h3>
        <p className="text-sm text-[#404040] mb-6 p-3 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm" data-id="element-384">
          Omnibus chi-square across all four assets:{' '}
          <span className="text-[#C00000] font-medium" data-id="element-385">p = 0.006</span>, the
          assets do not perform equally.
        </p>
        <SignificanceFlow significanceLevels={[95, 90, 85]} data-id="element-386" />
        <table className="min-w-full" data-id="element-387">
          <thead data-id="element-388">
            <tr data-id="element-389">
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider w-64" data-id="element-390">
                Image Asset
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-391">
                <div className="mb-2 text-[#C00000]" data-id="element-392">95% Confidence Level</div>
                Z-Test vs Winner
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-393">
                Bayesian Best Probability
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-394">
            {sortedData.map((item, index) => <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-[#C00000]/5' : ''}`} onMouseEnter={() => {
            if (index === 0) setCurrentSignificance(95);else if (index === 1) setCurrentSignificance(90);else setCurrentSignificance(85);
          }} data-id="element-395">
                <td className="px-4 py-3" data-id="element-396">
                  <div className="flex items-center space-x-3" data-id="element-397">
                    <div className="h-10 w-16 flex-shrink-0 rounded-sm overflow-hidden bg-[#F9FAFB]" data-id="element-398">
                      <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-399" />
                    </div>
                    <span className="text-sm font-medium text-[#101010]" data-id="element-400">
                      {item.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-401">
                  {item.id === 3 ? <span className="inline-flex items-center bg-[#C00000]/10 text-[#C00000] font-medium px-2 py-0.5 rounded-sm text-xs uppercase tracking-wide" data-id="element-402">
                      Winner
                    </span> : <span className="text-[#C00000] font-medium" data-id="element-403">
                      p ={' '}
                      {item.id === 4 ? '0.084' : item.id === 2 ? '0.003' : '0.001'}
                    </span>}
                </td>
                <td className={`px-4 py-3 text-sm ${item.id === 3 ? 'text-[#C00000] font-medium' : 'text-[#737373]'}`} data-id="element-404">
                  <MiniDonutChart percentage={item.id === 3 ? 91.2 : item.id === 4 ? 8.5 : item.id === 2 ? 0.2 : 0.1} data-id="element-405" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Lifestyle audience significance matrix */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5] overflow-x-auto" data-id="element-406">
        <h3 className="text-lg font-medium text-[#101010] mb-4" data-id="element-407">
          Statistical Significance Matrix (Lifestyle audience)
        </h3>
        <p className="text-sm text-[#404040] mb-6 p-3 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm" data-id="element-408">
          Omnibus chi-square across all four assets:{' '}
          <span className="text-[#C00000] font-medium" data-id="element-409">p = 0.014</span>, the
          assets do not perform equally.
        </p>
        <table className="min-w-full" data-id="element-410">
          <thead data-id="element-411">
            <tr data-id="element-412">
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider w-64" data-id="element-413">
                Image Asset
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-414">
                <div className="mb-2 text-[#C00000]" data-id="element-415">95% Confidence Level</div>
                Z-Test vs Winner
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-416">
                Bayesian Best Probability
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-417">
            {lifestyleRows.map(row => {
            const item = getById(row.id);
            if (!item) return null;
            return <tr key={row.id} className={`hover:bg-gray-50 transition-colors ${row.winner ? 'bg-[#C00000]/5' : ''}`} data-id="element-418">
                  <td className="px-4 py-3" data-id="element-419">
                    <div className="flex items-center space-x-3" data-id="element-420">
                      <div className="h-10 w-16 flex-shrink-0 rounded-sm overflow-hidden bg-[#F9FAFB]" data-id="element-421">
                        <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-422" />
                      </div>
                      <span className="text-sm font-medium text-[#101010]" data-id="element-423">
                        {item.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm" data-id="element-424">
                    {row.winner ? <span className="inline-flex items-center bg-[#C00000]/10 text-[#C00000] font-medium px-2 py-0.5 rounded-sm text-xs uppercase tracking-wide" data-id="element-425">
                        Winner
                      </span> : <span className="text-[#C00000] font-medium" data-id="element-426">
                        p = {row.z}
                      </span>}
                  </td>
                  <td className={`px-4 py-3 text-sm ${row.winner ? 'text-[#C00000] font-medium' : 'text-[#737373]'}`} data-id="element-427">
                    <MiniDonutChart percentage={row.bayes} data-id="element-428" />
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Between-segment comparison, the key evidence table */}
      <div className="bg-white rounded-sm shadow-[0_6px_20px_rgba(192,0,0,0.10)] border-2 border-[#C00000] overflow-hidden" data-id="element-429">
        <div className="bg-[#C00000] px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-430">
          <h3 className="text-lg font-semibold text-white" data-id="element-431">
            Between-Segment Comparison (Core Evidence Layer)
          </h3>
          <span className="inline-flex items-center self-start sm:self-auto bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-id="element-432">
            Key evidence · Illustrative
          </span>
        </div>
        <div className="overflow-x-auto" data-id="element-433">
          <table className="min-w-full" data-id="element-434">
            <thead data-id="element-435">
              <tr className="bg-[#C00000]/5" data-id="element-436">
                <th className="px-6 py-3 text-left text-xs font-bold text-[#101010] uppercase tracking-wider w-64" data-id="element-437">
                  Asset
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#101010] uppercase tracking-wider" data-id="element-438">
                  Direction of difference
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#101010] uppercase tracking-wider" data-id="element-439">
                  Significance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-440">
              {betweenSegmentRows.map(row => {
              const item = getById(row.id);
              if (!item) return null;
              return <tr key={row.id} className="hover:bg-[#C00000]/5 transition-colors" data-id="element-441">
                    <td className="px-6 py-4" data-id="element-442">
                      <div className="flex items-center space-x-3" data-id="element-443">
                        <div className="h-10 w-16 flex-shrink-0 rounded-sm overflow-hidden bg-[#F9FAFB]" data-id="element-444">
                          <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-445" />
                        </div>
                        <span className="text-sm font-medium text-[#101010]" data-id="element-446">
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#404040]" data-id="element-447">
                      {row.direction}
                    </td>
                    <td className="px-6 py-4 text-sm" data-id="element-448">
                      <span className="text-[#C00000] font-semibold" data-id="element-449">
                        p = {row.p}
                      </span>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-[#C00000]/5 border-t border-[#C00000]/10" data-id="element-450">
          <p className="text-sm font-medium text-[#101010] leading-relaxed" data-id="element-451">
            Every creative shows a statistically significant interaction
            difference between the two segments. Creative-audience fit is
            measurable, in this illustrative example.
          </p>
        </div>
      </div>

      <BayesianDistributionChart data={data} data-id="element-452" />
      <FloatingLegend data-id="element-453" />
    </div>;
};